import { useWeb3ModalProvider } from '@web3modal/ethers/vue';
import { INTERNAL_SERVER_ERROR, USER_REJECT_ACTION_CODE } from '~/utils/constants';
import useEnvs from './useEnvs';
import { hexlify, toBeArray } from 'ethers';

type AddTokenToWalletOptions = {
  address: string;
  symbol?: string;
  decimals: number;
  image?: string;
  type?: string;
  tokenId?: string;
};

export default () => {
  const { walletProvider } = useWeb3ModalProvider();
  const { blockchain } = useEnvs();
  const addTokenToWallet = async (options: AddTokenToWalletOptions) => {
    if (walletProvider.value) {
      try {
        await walletProvider.value.request({
          method: 'wallet_watchAsset',
          params: {
            type: options.type || 'ERC20',
            options
          }
        });
      } catch (error: any) {
        if (error.code === USER_REJECT_ACTION_CODE) {
          return;
        }
        throw new Error(error?.message || INTERNAL_SERVER_ERROR);
      }
    }
  };

  const chainIdToName = (chainId: number) => {
    switch (chainId) {
      case 56:
        return 'BNB Smart Chain Mainnet';
      case 97:
        return 'BNB Smart Chain Testnet';
      default:
        return 'unknown chain';
    }
  };

  const chainIdToSymbol = (chainId: number) => {
    switch (chainId) {
      case 56:
        return 'BNB';
      case 97:
        return 'tBNB';
      default:
        return 'unknown symbol';
    }
  };

  const addNetwork = () => {
    const { networkId, blockScanUrl, nodeUrl } = blockchain;
    const symbol = chainIdToSymbol(networkId);

    return walletProvider.value?.request({
      method: 'wallet_addEthereumChain',
      params: [
        {
          chainId: hexlify(toBeArray(networkId)),
          chainName: chainIdToName(networkId),
          nativeCurrency: { name: symbol, symbol: symbol, decimals: 18 },
          rpcUrls: [nodeUrl + '/'], // must end with /
          blockExplorerUrls: [blockScanUrl + '/']
        }
      ]
    });
  };

  const switchNetwork = async () => {
    const { networkId } = blockchain;

    try {
      return await walletProvider.value?.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: hexlify(toBeArray(networkId)) }]
      });
    } catch (e: unknown) {
      // network not present in metamask
      if ((e as { code?: number })?.code === 4902) return await addNetwork();
      throw e;
    }
  };

  return {
    addTokenToWallet,
    chainIdToName,
    chainIdToSymbol,
    addNetwork,
    switchNetwork
  };
};
